<template>
  <div>
    <v-text-field prepend-inner-icon="mdi-magnify" v-model="servicesSearch" placeholder="Search Services" clearable @click:clear="clearSearch" density="compact" variant="outlined" />

    <div v-if="props.usage != 'scan-filter'" class="mb-4">
      <v-btn @click="selectedServicesShow = false" class="text-none rounded-md mr-2 border-md font-weight-medium" :class="{ 'activeBtn': selectedServicesShow == false }" variant="flat">View All Services</v-btn>
      <v-btn @click="selectedServicesShow = true" class="text-none rounded-md border-md font-weight-medium" :class="{ 'activeBtn': selectedServicesShow == true }" variant="flat">View Selected Services {{ selectedServicesIds.length > 0 ? `(${selectedServicesIds.length})` : '' }}</v-btn>
    </div>

    <v-infinite-scroll v-if="!(selectedServicesShow && selectedServicesIds == 0 )" :key="reloadInfiniteScroll" color="black" mode="manual" @load="loadMore" load-more-text="Load More Services">
      <v-table style="height: 100%; max-height: 450px" fixed-header>
        <thead>
        <tr class="font-weight-bold text-subtitle-1">
          <th class="py-n2 pl-0"><v-checkbox color="#F7CC4F" class="font-weight-bold" hide-details label="&nbsp; Services" v-model="checkAllServices" @change="checkAll" density="compact" /></th>
          <th v-if="props.usage != 'scan-filter'" class="py-n2 text-center font-weight-bold">Service ID</th>
          <th class="py-n2 text-center font-weight-bold">View Service</th>
        </tr>
        </thead>
        <tbody>
        <tr class="text-subtitle-1" v-for="(service, index) in services" :key="service.serviceId">
          <td class="pl-0">
            <v-checkbox color="#F7CC4F" density="compact" v-model="selectedServicesIds" :value="service.serviceId" hide-details>
              <template v-slot:label>
                <div class="text-subtitle-1">&nbsp; {{serviceName(service.serviceName)}}</div>
              </template>
            </v-checkbox>
          </td>
          <td v-if="props.usage != 'scan-filter'" class="text-center">{{ service.serviceId }}</td>
          <td class="text-center font-weight-bold text-decoration-underline" style="color: #C37C1B">
            <span class="cursor-pointer" @click="goEditServicePage(service.serviceId)">View</span>
          </td>
        </tr>
        </tbody>
      </v-table>

      <template v-slot:empty>
        No more results
      </template>
    </v-infinite-scroll>
    <div v-else class="text-subtitle-1 text-center pa-4">
      No selected service
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, PropType } from 'vue';
import { useRouter,useRoute } from 'vue-router';
import crApi from "../service/CrApi";
import securityService from "../service/SecurityService";
import spinner from "../component/SpinnerComponent.vue";

const router = useRouter();
const route: any = useRoute();
const props = defineProps({
  modelValue: Array as PropType<string[]>,
  servicesSearchText: String,
  selectAll: Boolean,
  selectedServicesCheckbox: Boolean,
  deletedServices: Boolean,
  usage: String
});

const emit = defineEmits(['update:modelValue', 'update:selectAll']);

const isLoading = ref(false);
const offset = ref(0);
const limit = ref(6);
const servicesSearch = ref(props.servicesSearchText || '');
const debouncedServicesSearch = ref(servicesSearch.value);

const checkAllServices = ref(props.selectAll ? true : false);
const services = ref([]);
const selectedServicesShow = ref(false);
const selectedServicesIds = ref<any>(props.modelValue);
const reloadInfiniteScroll = ref(1);

onMounted(async () => {
  await loadMore({}, true, false);
  if(route.query?.showSelectedService == 1) {
    selectedServicesShow.value = true;
  } else if(route.query?.showSelectedService == 0) {
    selectedServicesShow.value = false;
  }
  if(props.usage == 'scan-filter') {
    if(props.selectAll) {
      selectedServicesIds.value = services.value.map(service => service.serviceId);
    }
  }
  updateCheckAllState();
});

watch(servicesSearch, (newSearch) => {
  debounceSearch(newSearch);
});

watch(() => selectedServicesIds.value, () => {
  updateCheckAllState();
  emit('update:modelValue', selectedServicesIds.value);
});

watch(() => selectedServicesShow.value, async () =>{
  services.value = [];
  offset.value = 0;
  reloadInfiniteScroll.value++;

  await loadMore({}, true, false);
  updateCheckAllState();
});

async function loadMore({ done }: { done?: (arg: string) => void } = {}, firstLoad = false, search = false) {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    let response;
    if(!selectedServicesShow.value) {
      response = await crApi.requestV2('service/retrieve/', {
        sortBy: 'servicename',
        response: 'extra',
        sortOrder: 'asc',
        website_token: securityService.getToken(),
        excludeTypes: ['webview', 'scanwedge'],
        offset: offset.value,
        limit: limit.value,
        serviceName: debouncedServicesSearch.value
      });
    } else {
      if(selectedServicesIds.value.length > 0) {
        response = await crApi.requestV2('service/retrieve/', {
          website_token: securityService.getToken(),
          serviceName: debouncedServicesSearch.value,
          offset: offset.value,
          limit: limit.value,
          serviceId: selectedServicesIds.value,
        });
      } else {
        response = { status: 1, service: [] };
      }
    }

    if (response && response.status === 1) {
      services.value.push(...response.service);
      offset.value += limit.value;

      if (done) {
        done(response.service.length < limit.value ? 'empty' : 'done');
      }
    }
  } catch (error) {
    console.error('Error loading services:', error);
  } finally {
    isLoading.value = false;
    updateCheckAllState();
  }
}

let debounceTimeout: ReturnType<typeof setTimeout>;
function debounceSearch(newSearch: string) {
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    debouncedServicesSearch.value = newSearch;
    offset.value = 0;
    services.value = [];
    reloadInfiniteScroll.value++;
    loadMore({}, false, true);
  }, 1000);
}

function checkAll() {
  if (checkAllServices.value) {
    selectedServicesIds.value = services.value.map(service => service.serviceId);
  } else {
    selectedServicesIds.value = [];
  }
  emit('update:modelValue', selectedServicesIds.value);
  emit('update:selectAll', checkAllServices.value);
}

function updateCheckAllState() {
  if (services.value.length > 0 && services.value.every(service => selectedServicesIds.value.includes(service.serviceId))) {
    checkAllServices.value = true;
  } else {
    checkAllServices.value = false;
  }
}

function serviceName(name: string) {
  return name.length > 29 ? `${name.substring(0, 29)}...` : name;
}
function goEditServicePage(serviceId) {
  router.push({ path: `/account/services/${serviceId}/type/` });
}

function clearSearch() {
  servicesSearch.value = '';
  checkAllServices.value = selectedServicesIds.value.length === services.value.length;
}
</script>

<style lang="scss" scoped>
:deep(.v-btn) {
  outline: none;
}

:deep(.v-infinite-scroll__side) {
  padding: 3px 0px;
  overflow-x: hidden !important;
}

:deep(.v-infinite-scroll__side .v-btn) {
  text-transform: none !important;
  font-weight: bold;
}

.activeBtn {
  background-color: #FEF8E3;
  border: 2px solid #EBB824 !important;
}

</style>
